import type { Ref } from "react";
import {
  getReadableShiftCodeTextColor,
  getSettingShiftCodeDisplayText,
  getSettingShiftCodeFontSize,
} from "@utils/fontSizeUtils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TimePicker } from "@/components/ui/time-picker";
import { cn } from "@/utils/cn";
import { SLOT_OPTIONS } from "./shift-code-add-dialog-utils";

function FieldError({
  id,
  message,
}: {
  readonly id: string;
  readonly message?: string;
}) {
  if (!message) return null;
  return (
    <p
      id={id}
      aria-live="polite"
      className="min-h-5 text-xs font-medium leading-5 text-destructive"
    >
      {message}
    </p>
  );
}

export function ShiftCodeField({
  inputRef,
  value,
  color,
  error,
  codeDisabled = false,
  colorDisabled = false,
  onChange,
  onColorChange,
}: {
  readonly inputRef?: Ref<HTMLInputElement>;
  readonly value: string;
  readonly color: string;
  readonly error?: string;
  readonly codeDisabled?: boolean;
  readonly colorDisabled?: boolean;
  readonly onChange: (value: string) => void;
  readonly onColorChange: (value: string) => void;
}) {
  const trimmedValue = value.trim();
  const previewText = getSettingShiftCodeDisplayText(trimmedValue);
  const previewTextColor = getReadableShiftCodeTextColor(color);
  const errorId = "new-shift-code-error";
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor="new-shift-code">근무코드</Label>
        <span className="font-mono text-xs tabular-nums text-muted-foreground">
          {color}
        </span>
      </div>
      <div className="grid min-w-0 grid-cols-[auto_1fr] items-center gap-2">
        <label
          htmlFor="new-shift-color"
          className="relative mx-auto flex h-8 w-8 cursor-pointer items-center justify-center overflow-hidden rounded text-white shadow-sm ring-1 ring-border"
          style={{ backgroundColor: color, color: previewTextColor }}
          title="색상 변경"
        >
          <span className="sr-only">색상</span>
          <span
            className="font-pretendard font-bold whitespace-nowrap"
            title={trimmedValue || undefined}
            style={{
              fontSize: getSettingShiftCodeFontSize(previewText),
            }}
          >
            {previewText}
          </span>
          <input
            id="new-shift-color"
            name="shift-color"
            type="color"
            value={color}
            disabled={colorDisabled}
            className="absolute inset-0 cursor-pointer opacity-0"
            aria-label="근무코드 색상"
            onChange={(event) => onColorChange(event.currentTarget.value)}
          />
        </label>
        <Input
          ref={inputRef}
          id="new-shift-code"
          name="shift-code"
          value={value}
          disabled={codeDisabled}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          autoComplete="off"
          spellCheck={false}
          placeholder="예: D1"
          maxLength={3}
          className="h-10 min-w-0 bg-white"
          onChange={(event) => onChange(event.currentTarget.value.slice(0, 3))}
        />
      </div>
      <FieldError id={errorId} message={error} />
    </div>
  );
}

export function ShiftNameField({
  value,
  error,
  disabled = false,
  onChange,
}: {
  readonly value: string;
  readonly error?: string;
  readonly disabled?: boolean;
  readonly onChange: (value: string) => void;
}) {
  const errorId = "new-shift-name-error";
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor="new-shift-name">이름</Label>
      <Input
        id="new-shift-name"
        name="shift-name"
        value={value}
        disabled={disabled}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : undefined}
        autoComplete="off"
        placeholder="예: 데이 보조"
        maxLength={20}
        className="h-10 min-w-0 bg-white"
        onChange={(event) => onChange(event.currentTarget.value.slice(0, 20))}
      />
      <FieldError id={errorId} message={error} />
    </div>
  );
}

export function ShiftDescriptionField({
  value,
  disabled = false,
  onChange,
}: {
  readonly value: string;
  readonly disabled?: boolean;
  readonly onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor="new-shift-description">설명</Label>
      <Textarea
        id="new-shift-description"
        name="shift-description"
        value={value}
        disabled={disabled}
        autoComplete="off"
        placeholder="예: 병동 교육 지원"
        rows={3}
        className="min-h-[76px] resize-none bg-white"
        onChange={(event) => onChange(event.currentTarget.value)}
      />
    </div>
  );
}

export function SlotField({
  value,
  disabled,
  readonlySlotLabel,
  onChange,
}: {
  readonly value: number;
  readonly disabled: boolean;
  readonly readonlySlotLabel?: string | null;
  readonly onChange: (value: number) => void;
}) {
  const showReadonlySlot = disabled && Boolean(readonlySlotLabel);

  return (
    <div className="flex flex-col gap-2">
      <Label>구분</Label>
      <div
        role="radiogroup"
        aria-label="구분"
        aria-disabled={disabled}
        className="flex flex-wrap justify-start gap-2"
      >
        {SLOT_OPTIONS.map((item) => {
          const isSelected = !showReadonlySlot && item.value === value;

          return (
            <button
              key={item.value}
              type="button"
              role="radio"
              aria-checked={isSelected}
              disabled={disabled}
              onClick={() => {
                onChange(item.value);
              }}
              className={cn(
                "flex h-9 min-w-[88px] cursor-pointer items-center justify-center rounded-full border bg-white px-4 text-sm font-semibold text-[#344054] transition-colors",
                isSelected
                  ? "border-[#0E59C6] text-[#0E59C6]"
                  : "border-[#D0D5DD] hover:border-[#98A2B3]",
                disabled && "cursor-not-allowed opacity-45"
              )}
            >
              <span className="min-w-0 truncate">{item.label}</span>
            </button>
          );
        })}
        {showReadonlySlot ? (
          <button
            type="button"
            role="radio"
            aria-checked
            disabled
            className="flex h-9 min-w-[88px] cursor-not-allowed items-center justify-center rounded-full border border-[#0E59C6] bg-white px-4 text-sm font-semibold text-[#0E59C6] opacity-45"
          >
            <span className="min-w-0 truncate">{readonlySlotLabel}</span>
          </button>
        ) : null}
      </div>
      {disabled ? (
        <p className="text-xs leading-5 text-muted-foreground">
          기본 근무코드는 타입과 구분을 변경할 수 없습니다.
        </p>
      ) : null}
    </div>
  );
}

export function WorkTimeField({
  enabled,
  startTime,
  endTime,
  onStartTimeChange,
  onEndTimeChange,
}: {
  readonly enabled: boolean;
  readonly startTime: string;
  readonly endTime: string;
  readonly onStartTimeChange: (value: string) => void;
  readonly onEndTimeChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <Label>근무시간</Label>
      {enabled ? (
        <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3">
          <TimePicker
            value={startTime}
            onChange={onStartTimeChange}
            ariaLabel="근무 시작 시간"
          />
          <span className="text-sm font-medium text-muted-foreground">~</span>
          <TimePicker
            value={endTime}
            onChange={onEndTimeChange}
            ariaLabel="근무 종료 시간"
          />
        </div>
      ) : (
        <div className="flex h-10 items-center rounded-md border border-border bg-white px-3 text-sm text-muted-foreground">
          -
        </div>
      )}
    </div>
  );
}
